import { omit } from 'lodash';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { shoppingBagErrorCodes } from '@/errorCodes/orderApp';
import { getCheckoutLineUnitPriceWithoutDiscount } from '@/utils/checkout';
import { isPathMatch } from '@/utils/urlUtils';

import { SectionHeader } from '@/components/SectionHeader/SectionHeader';
import type {
  CheckoutLineFragment,
  ProductVariant,
} from '@/graphql/saleor/api';
import {
  type CheckoutLine,
  useShoppingBagProductsQuery,
} from '@/graphql/saleor/api';
import { useCheckout } from '@/hooks/useCheckout';
import { useDeviceDetect } from '@/hooks/useDeviceDetect';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useIsMdUp } from '@/hooks/useMedia';
import { useOnRouteChange } from '@/hooks/useOnRouteChange';
import { useProcessingLine } from '@/hooks/useProcessingLine';
import { useRouter } from '@/hooks/useRouter';
import { getDropdownValue, getMultiselectValue } from '@/lib/attributes';
import { getLinesIdWithNotSelectedBraceletSize } from '@/lib/checkout';
import {
  CARDS_IMAGE_SIZE,
  NEXT_PUBLIC_IMAGES_FORMAT,
  WARRANTY_EXTENSION_VARIANT_ID,
} from '@/lib/const';
import { getErrorMessage, parseErrors } from '@/lib/errors';
import { genericErrorHandler } from '@/lib/forms/helpers';
import { EVENT_NAME } from '@/lib/gtm/const';
import { mapEdgesToItems } from '@/lib/maps';
import { getMetaValue } from '@/lib/metadata/helpers';
import type { UrlOpts } from '@/lib/paths';
import { excludeProductType, getProductType } from '@/lib/product';
import { checkIfVatIncluded, getRegionFromLocale } from '@/lib/regions';
import type {
  ApiErrorWithType,
  Maybe,
  ShoppingBagProductWithCheckoutLineId,
} from '@/lib/types';
import { AddAWarranty, commonMessages } from '@/messages/common';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import { checkoutActions } from '@/states/checkout';
import { WatchProductAction } from '@/views/ShoppingBagView/components/ShoppingBagCard/components/WatchProductAction/WatchProductAction';
import {
  getRelatedBuckle,
  getRelatedProductWithCheckoutLineId,
  hasMetadataKey,
} from '@/views/ShoppingBagView/components/ShoppingBagCard/utils';
import { ShoppingBagStrapDrawer } from '@/views/ShoppingBagView/components/ShoppingBagStrapDrawer';

import { AddStrapButton } from '../AddStrapButton/AddStrapButton';
import { Icon } from '../Icon';
import { Loader } from '../Loader';
import { Overlay } from '../Overlay';
import { CustomErrors, ServerErrors } from '../ServerErrors';
import { SubTotalMobileSummary } from '../TransactionSummary/SubTotalMobileSummary';
import { TransactionSummary } from '../TransactionSummary/TransactionSummary';
import {
  EmptyShoppingBag as EmptyShoppingBagDesktop,
  ShoppingBagItem,
} from './components';
import { EmptyShoppingBagMobile } from './components/EmptyShoppingBagMobile';
import ErrorShoppingBag from './components/ErrorShoppingBag/ErrorShoppingBag';

import * as Sc from '../Drawer/Drawer.styles';
import * as M from './messages';
import * as S from './ShoppingBagDrawer.styles';

interface ShoppingBagDrawerProps {
  className?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

export const ShoppingBagDrawer = ({
  className,
  isOpen = false,
  onClose,
}: ShoppingBagDrawerProps) => {
  const { deviceType } = useDeviceDetect();
  const [drawerOpen, setDrawerOpen] = useState<boolean>(isOpen);
  const { formatMessage } = useIntl();
  const {
    lineAdd,
    lineDelete,
    refresh,
    snapshot: {
      checkout,
      errors: checkoutErrors,
      isProcessing,
      notification,
      preOrderData,
      voucherType,
    },
  } = useCheckout();
  const { locale, pathname, paths, query: queryParams } = useRouter();
  const [errors, setErrors] = useState<ApiErrorWithType[]>([]);
  const checkoutId = queryParams?.checkout;
  const isAbandonedCartView =
    isPathMatch(pathname, paths.store.shoppingBag.asPath()) &&
    checkoutId &&
    typeof checkoutId === 'string';
  const { sendEcommerceEventByLines } = useGoogleAnalytics();
  const {
    country: { code },
  } = getRegionFromLocale(locale);
  const { isBeingProcessed, processLine } = useProcessingLine();
  const { query } = useRegionProvider();
  const [braceletSizeErrorLinesId, setBraceletSizeErrorLinesId] = useState<
    string[]
  >([]);
  const shoppingBagItemRefs = useRef<Record<string, HTMLElement | null>>({});
  useEffect(() => {
    if (drawerOpen) {
      (async () => {
        try {
          if (typeof refresh === 'function') {
            await refresh(true);
          } else {
            console.error('refresh is not a function');
          }
        } catch (error) {
          console.error('Error calling refresh:', error);
        }
      })();
    }
  }, [drawerOpen]);
  const isMdUp = useIsMdUp();

  const errorHandler = genericErrorHandler({
    formatMessage,
    apiErrorsHandler: (errors, errorType) =>
      setErrors(
        errors.map((error) => ({
          type: errorType,
          ...error,
        })),
      ),
    errorType: 'checkout',
  });

  const handleCloseDialog = () =>
    setDrawerOpen(() => {
      onClose && onClose();
      checkoutActions.notify(null);
      return false;
    });

  useOnRouteChange(() =>
    setDrawerOpen(() => {
      onClose && onClose();
      checkoutActions.notify(null);
      return false;
    }),
  );

  const [currentWatch, setCurrentWatch] =
    useState<ShoppingBagProductWithCheckoutLineId | null>(null);
  const compatibleStrapIds = getMultiselectValue(
    currentWatch?.product.compatibleStraps,
  );

  const checkoutLines =
    checkout?.lines
      ?.filter((line) =>
        excludeProductType(
          line.variant.product.productType,
          'colorado-tax-dummy',
        ),
      )
      .map((line) => ({
        sku: line.variant.sku ?? '',
        checkoutLineId: line.id,
      })) ?? [];

  const checkoutVariantSkus = checkoutLines.map(({ sku }) => sku);

  const { data, loading } = useShoppingBagProductsQuery({
    variables: {
      filter: {
        sku: checkoutVariantSkus,
      },
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
      thumbnailSize: CARDS_IMAGE_SIZE,
      ...query,
    },
    skip: !checkoutVariantSkus.length,
  });

  const mappedProductVariants = mapEdgesToItems(data?.productVariants);
  const productVariants = checkoutLines
    .map((line) => {
      const product = mappedProductVariants.find((p) => p.sku === line.sku);

      // If matching variant is not found (this could happen if a line was
      // filtered out, e.g. the Colorado tax dummy line), we skip it to avoid
      // spreading an undefined value which would crash the component.
      if (!product) return undefined;

      return {
        ...product,
        checkoutLineId: line.checkoutLineId,
      };
    })
    .filter(
      (
        productVariant,
      ): productVariant is ShoppingBagProductWithCheckoutLineId =>
        productVariant !== undefined,
    );

  const areBraceletSizeErrorsSet = () => {
    const linesIdsWithNotSelectedBraceletSize =
      getLinesIdWithNotSelectedBraceletSize(checkout?.lines);

    if (linesIdsWithNotSelectedBraceletSize?.length) {
      // Only update state (and scroll) when the list actually changes
      setBraceletSizeErrorLinesId((prev) => {
        const isSameLength =
          prev.length === linesIdsWithNotSelectedBraceletSize.length;
        const isSameContent =
          isSameLength &&
          prev.every(
            (id, idx) => id === linesIdsWithNotSelectedBraceletSize[idx],
          );

        if (!isSameContent) {
          shoppingBagItemRefs.current[
            linesIdsWithNotSelectedBraceletSize[0]
          ]?.scrollIntoView();
          return linesIdsWithNotSelectedBraceletSize;
        }
        return prev;
      });
      return false;
    }

    setBraceletSizeErrorLinesId((prev) => (prev.length ? [] : prev));

    return true;
  };

  useEffect(() => {
    const isVatIncluded = checkIfVatIncluded(checkout?.channel?.slug);
    if (isOpen) {
      sendEcommerceEventByLines({
        coupon: checkout?.voucherCode ?? undefined,
        event: EVENT_NAME.INTENTION_VIEW_CART,
        lines: checkout?.lines as CheckoutLineFragment[],
        totalValue: isVatIncluded
          ? checkout?.totalPrice?.gross?.amount
          : checkout?.totalPrice?.net?.amount,
      });
    }
    setDrawerOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    setBraceletSizeErrorLinesId([]);
  }, [checkout?.lines]);

  useEffect(() => {
    if (notification) {
      setDrawerOpen(true);
    }
  }, [notification]);

  const { warrantyExtensionCampaignEnable } = useConfigProvider();

  const nonShoppingBagErrors = errors.filter(
    (item) => !shoppingBagErrorCodes?.includes(item.code),
  );
  const shoppingBagErrors = parseErrors(
    [
      ...(checkoutErrors?.filter((item) =>
        shoppingBagErrorCodes?.includes(item.code),
      ) || []),
      ...(errors?.filter((item) =>
        shoppingBagErrorCodes?.includes(item.code),
      ) || []),
    ],
    formatMessage,
  );

  return !drawerOpen ? null : (
    <Overlay
      className='modal-open'
      data-test-id='ShoppingBagDrawerOverlay'
      onClick={handleCloseDialog}
    >
      <Sc.Wrapper
        $hasHeader={true}
        $side='right'
        className={className}
        data-test-id='ShoppingBagDrawerWrapper'
      >
        <Sc.HeaderWrapper
          $hasMobileVersion={true}
          $isMobile={false}
          data-test-id='ShoppingBagDrawerHeaderWrapper'
        >
          <Sc.Header data-test-id='ShoppingBagDrawerHeader'>
            <SectionHeader
              color='black'
              colorized={!isMdUp}
              disableGutters
              sizeDesktop={24}
              sizeMobile={24}
              text={formatMessage(M.messages.shoppingBag)}
              textAlign='center'
            />
          </Sc.Header>
          <Sc.CloseButton
            data-test-id='ShoppingBagDrawerCloseButton'
            onClick={handleCloseDialog}
          >
            <Sc.CloseIcon data-test-id='ShoppingBagDrawerCloseIcon' />
          </Sc.CloseButton>
        </Sc.HeaderWrapper>
        <ServerErrors
          data-test-id='ShoppingBagDrawerServerErrors'
          errors={nonShoppingBagErrors}
          my={0}
          setErrors={setErrors}
        />
        {(() => {
          if (loading) return <Loader data-test-id='ShoppingBagDrawerLoader' />;
          else if (!checkout?.lines?.length) {
            return deviceType === 'mobile' ? (
              <>
                <Sc.HeaderWrapper
                  $hasMobileVersion={true}
                  $isMobile={true}
                  data-test-id='ShoppingBagDrawerHeaderWrapper'
                >
                  <Sc.Header data-test-id='ShoppingBagDrawerHeader'>
                    <SectionHeader
                      color='black'
                      colorized={!isMdUp}
                      disableGutters
                      sizeDesktop={24}
                      sizeMobile={24}
                      text={formatMessage(M.messages.shoppingBag)}
                      textAlign='center'
                    />
                  </Sc.Header>
                  <Sc.CloseButton
                    data-test-id='ShoppingBagDrawerCloseButton'
                    onClick={handleCloseDialog}
                  >
                    <Sc.CloseIcon data-test-id='ShoppingBagDrawerCloseIcon' />
                  </Sc.CloseButton>
                </Sc.HeaderWrapper>
                <ErrorShoppingBag />
                <EmptyShoppingBagMobile />
              </>
            ) : (
              <>
                <ErrorShoppingBag />
                <EmptyShoppingBagDesktop data-test-id='ShoppingBagDrawerEmptyShoppingBag' />
              </>
            );
          } else
            return (
              <>
                <Sc.Body data-test-id='ShoppingBagDrawerBody'>
                  {typeof notification === 'string' && (
                    <S.AlreadyInBagText data-test-id='ShoppingBagDrawerAlreadyInBagText'>
                      {notification}
                    </S.AlreadyInBagText>
                  )}
                  <ErrorShoppingBag />

                  <S.ProductsWrap data-test-id='ShoppingBagDrawerProductsGrid'>
                    <Sc.HeaderWrapper
                      $hasMobileVersion={true}
                      $isMobile={true}
                      data-test-id='ShoppingBagDrawerHeaderWrapper'
                    >
                      <Sc.Header data-test-id='ShoppingBagDrawerHeader'>
                        <SectionHeader
                          color='black'
                          colorized={!isMdUp}
                          disableGutters
                          sizeDesktop={24}
                          sizeMobile={24}
                          text={formatMessage(M.messages.shoppingBag)}
                          textAlign='center'
                        />
                      </Sc.Header>
                      <Sc.CloseButton
                        data-test-id='ShoppingBagDrawerCloseButton'
                        onClick={handleCloseDialog}
                      >
                        <Sc.CloseIcon data-test-id='ShoppingBagDrawerCloseIcon' />
                      </Sc.CloseButton>
                    </Sc.HeaderWrapper>
                    {productVariants
                      .filter((productVariant) => {
                        const { checkoutLineId } = productVariant;
                        const line = checkout.lines?.find(
                          (line) => line.id === checkoutLineId,
                        );
                        return !line?.relatedProductWatchId;
                      })
                      .filter((productVariant) =>
                        excludeProductType(
                          productVariant.product?.productType,
                          'gift',
                        ),
                      )
                      .filter((productVariant) =>
                        excludeProductType(
                          productVariant.product?.productType,
                          'buckle',
                        ),
                      )
                      .filter((productVariant) =>
                        excludeProductType(
                          productVariant.product?.productType,
                          'colorado-tax-dummy',
                        ),
                      )
                      .filter((productVariant) => !!productVariant.id)
                      .map((productVariant) => {
                        const { checkoutLineId, product } = productVariant;
                        const errorMessage = productVariant
                          ? getErrorMessage(
                              omit(
                                productVariant,
                                checkoutLineId,
                              ) as ProductVariant,
                              shoppingBagErrors,
                            )
                          : '';

                        const line = checkout.lines?.find(
                          (line) => line.id === checkoutLineId,
                        );

                        const { productType } = product;

                        const isWatch = getProductType(productType) === 'watch';

                        const relatedStraps = checkout.lines?.filter(
                          (checkoutLine) => {
                            return (
                              checkoutLine.variant.product.productType.slug !==
                                'warranty' &&
                              checkoutLine.relatedProductWatchId ===
                                checkoutLineId
                            );
                          },
                        );

                        const relatedBuckle: Maybe<CheckoutLineFragment> =
                          line && getRelatedBuckle(checkout.lines, line);

                        const showBuckleSizeSelector =
                          productVariant.product?.strapColor?.values[0]
                            ?.name === 'Metal bracelet';

                        const hasBraceletSizeNotSelectedError =
                          !!braceletSizeErrorLinesId.find(
                            (id) => id === line?.id,
                          );

                        const hasRelatedStrap = !!relatedStraps?.length;

                        const hasRelatedCard =
                          hasRelatedStrap &&
                          line?.variant.product.productType.name.toLowerCase() ===
                            'watch';

                        const relatedStrapWithCheckoutLineId =
                          getRelatedProductWithCheckoutLineId(
                            checkout.lines,
                            checkoutLineId,
                            'watch',
                            'strap',
                          );

                        const relatedBuckleToRelatedStrap =
                          checkout.lines?.find(
                            (checkoutLine) =>
                              checkoutLine.id ===
                              relatedStrapWithCheckoutLineId?.relatedProductBuckleId,
                          );

                        if (!line || !productVariant.product) {
                          return null;
                        }
                        const hasRelatedExtendWarranty = hasMetadataKey(
                          line as CheckoutLine,
                          'has.warranty_ext',
                        );
                        const relatedWarrantyWithCheckoutLineId =
                          getRelatedProductWithCheckoutLineId(
                            checkout.lines,
                            line?.id,
                            'watch',
                            'warranty',
                          );
                        const hasExtendWarranty =
                          hasRelatedExtendWarranty &&
                          relatedWarrantyWithCheckoutLineId;
                        const warrantyExtensionDuration =
                          getDropdownValue(
                            productVariant.product.warrantyExtensionDuration,
                          ) || getMetaValue(line.metadata, 'warranty.duration');

                        return (
                          <Fragment key={checkoutLineId}>
                            {errorMessage && (
                              <CustomErrors message={errorMessage} />
                            )}
                            <ShoppingBagItem
                              ref={(el) => {
                                shoppingBagItemRefs.current[line.id] = el;
                              }}
                              channel={checkout?.channel?.slug}
                              data-test-id={`ShoppingBagDrawerItem-${checkoutLineId}`}
                              hasBraceletSizeNotSelectedError={
                                hasBraceletSizeNotSelectedError
                              }
                              hasExtendWarranty={hasRelatedExtendWarranty}
                              isDisabled={isProcessing}
                              isProcessing={isBeingProcessed(checkoutLineId)}
                              lineId={line?.id as string}
                              onDelete={() => {
                                processLine(
                                  checkoutLineId as string,
                                  lineDelete,
                                );
                              }}
                              onDeleteBuckle={() => {
                                processLine(
                                  relatedBuckle?.id as string,
                                  lineDelete,
                                );
                              }}
                              preOrderData={preOrderData}
                              price={getCheckoutLineUnitPriceWithoutDiscount(
                                line,
                                voucherType,
                              )}
                              productVariant={productVariant}
                              relatedBuckle={relatedBuckle}
                              showBuckleSizeSelector={showBuckleSizeSelector}
                              strapPack={
                                !!line.strapPackReference &&
                                line.strapPackReference !== ''
                              }
                              voucherType={voucherType}
                              warrantyExtensionDuration={
                                warrantyExtensionDuration
                              }
                            />
                            {hasRelatedCard &&
                              relatedStraps.map((relatedStrap) => {
                                const relatedBuckleToRelatedStrap =
                                  checkout.lines?.find(
                                    (checkoutLine) =>
                                      checkoutLine.id ===
                                      relatedStrap?.relatedProductBuckleId,
                                  );

                                return (
                                  relatedStrap && (
                                    <ShoppingBagItem
                                      key={relatedStrap?.id}
                                      channel={checkout?.channel?.slug}
                                      data-test-id={`ShoppingBagViewShoppingBagCard-${relatedStrap?.id}`}
                                      hasBraceletSizeNotSelectedError={
                                        hasBraceletSizeNotSelectedError
                                      }
                                      hasExtendWarranty={!!hasExtendWarranty}
                                      isDisabled={isProcessing}
                                      lineId={line.id}
                                      onDelete={(lineId) => {
                                        processLine(lineId, lineDelete);
                                      }}
                                      onDeleteBuckle={() => {
                                        processLine(
                                          relatedBuckleToRelatedStrap?.id as string,
                                          lineDelete,
                                        );
                                      }}
                                      price={getCheckoutLineUnitPriceWithoutDiscount(
                                        relatedStrap,
                                        voucherType,
                                      )}
                                      productVariant={{
                                        ...relatedStrap?.variant,
                                        checkoutLineId: relatedStrap?.id,
                                      }}
                                      relatedBuckle={
                                        relatedBuckleToRelatedStrap
                                      }
                                      showBuckleSizeSelector={false}
                                      strapPack={
                                        !!relatedStrap.strapPackReference &&
                                        relatedStrap.strapPackReference !== ''
                                      }
                                      voucherType={voucherType}
                                    />
                                  )
                                );
                              })}
                            {hasExtendWarranty && (
                              <ShoppingBagItem
                                channel={checkout?.channel?.slug}
                                data-test-id={`ShoppingBagDrawerExtendWarrantyItem-${checkoutLineId}`}
                                hasBraceletSizeNotSelectedError={
                                  hasBraceletSizeNotSelectedError
                                }
                                hasExtendWarranty={true}
                                isDisabled={isProcessing}
                                isProcessing={isBeingProcessed(
                                  relatedWarrantyWithCheckoutLineId.checkoutLineId,
                                )}
                                lineId={line?.id as string}
                                onDelete={() => {
                                  processLine(
                                    relatedWarrantyWithCheckoutLineId.checkoutLineId,
                                    lineDelete,
                                  );
                                }}
                                onDeleteBuckle={() => {
                                  processLine(
                                    relatedStrapWithCheckoutLineId?.relatedProductBuckleId as string,
                                    lineDelete,
                                  );
                                }}
                                price={getCheckoutLineUnitPriceWithoutDiscount(
                                  relatedWarrantyWithCheckoutLineId,
                                  voucherType,
                                )}
                                productVariant={{
                                  ...relatedWarrantyWithCheckoutLineId.variant,
                                  checkoutLineId:
                                    relatedWarrantyWithCheckoutLineId.checkoutLineId,
                                }}
                                relatedBuckle={relatedBuckleToRelatedStrap}
                                showBuckleSizeSelector={false}
                                voucherType={voucherType}
                                warrantyExtensionDuration={
                                  warrantyExtensionDuration
                                }
                              />
                            )}

                            {!preOrderData && isWatch && (
                              <S.Buttons>
                                <AddStrapButton
                                  data-test-id='ShoppingBagItemAddAStrap'
                                  onClick={() =>
                                    setCurrentWatch?.(productVariant)
                                  }
                                  orderCtaType={
                                    preOrderData ? 'PRE_ORDER' : 'ORDER'
                                  }
                                  productVariant={productVariant}
                                />
                                {warrantyExtensionDuration &&
                                  !hasExtendWarranty &&
                                  (warrantyExtensionCampaignEnable ? (
                                    <S.Notice>
                                      {formatMessage(
                                        commonMessages.warrantyExtensionCampaign,
                                      )}
                                    </S.Notice>
                                  ) : (
                                    <WatchProductAction
                                      buttonText={AddAWarranty(
                                        warrantyExtensionDuration,
                                      )}
                                      data-test-id='ShoppingBagItemAddAWarranty'
                                      onClick={() =>
                                        !isProcessing &&
                                        lineAdd?.(
                                          WARRANTY_EXTENSION_VARIANT_ID,
                                          'ORDER',
                                          line?.id as string,
                                        )
                                      }
                                    />
                                  ))}
                              </S.Buttons>
                            )}
                          </Fragment>
                        );
                      })}
                    <SubTotalMobileSummary
                      channel={checkout?.channel?.slug}
                      fragment={checkout}
                      salesTax={checkout?.totalPrice?.tax}
                      showTax={checkIfVatIncluded(checkout?.channel?.slug)}
                      voucherType={voucherType}
                    />
                  </S.ProductsWrap>
                </Sc.Body>
                <Sc.Footer data-test-id='ShoppingBagDrawerFooter'>
                  <TransactionSummary
                    areBraceletSizeErrorsSet={areBraceletSizeErrorsSet}
                    channel={checkout?.channel?.slug}
                    code={code}
                    errorHandler={errorHandler}
                    fragment={checkout}
                    isProcessing={isProcessing}
                    preOrderData={preOrderData}
                    salesTax={checkout?.totalPrice?.tax}
                    showButton={true}
                    showExpressButton={true}
                    showPadding={false}
                    showPaymentMethodsSection={false}
                    showSubtotalAndTaxes={false}
                    showTax={checkIfVatIncluded(checkout?.channel?.slug)}
                    showTaxCalculationInfo={false}
                    voucherType={voucherType}
                  />
                  <S.LinkButton
                    colorVariant='blue'
                    data-test-id='ShoppingBagDrawerLinkButton'
                    isFluid
                    label={M.ViewShoppingBagDetail}
                    linkProps={{
                      href: isAbandonedCartView
                        ? paths.store.shoppingBag.url({
                            query: queryParams as UrlOpts['query'],
                          })
                        : paths.store.shoppingBag.url(),
                    }}
                    variant='link'
                  >
                    <Icon name='chevronRight' size='m' variant='regular' />
                  </S.LinkButton>
                </Sc.Footer>
              </>
            );
        })()}
        <ShoppingBagStrapDrawer
          channel={checkout?.channel?.slug}
          compatibleStrapIds={compatibleStrapIds}
          data-test-id='ShoppingBagDrawerStrapDrawer'
          onClose={() => setCurrentWatch(null)}
          orderCtaType={preOrderData ? 'PRE_ORDER' : 'ORDER'}
          watch={currentWatch}
        />
      </Sc.Wrapper>
    </Overlay>
  );
};

ShoppingBagDrawer.displayName = 'ShoppingBagDrawer';
